import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import {
  DATA_SERVICE_TOKEN,
  EoSearchPageComponent,
  SearchPageOptionsService,
  STATUS,
} from 'ngx-eo-web-components';
import { SurgeryIdikaService } from '../../../../services/surgery-idika/surgery-idika.service';
import { IdikaSurgery } from '../../../core/models/idika-surgery.model';
import {
  addRequiredValidator,
  clearClinicDropdown,
  clearValidators,
  idikaSurgerySearchFormConfig,
  initSurgerySearchForm,
  setSearchParams,
} from '../../config/surgeries-idika-search-form.config';
import { BehaviorSubject } from 'rxjs';
import { IdikaSurgeriesTableConfigBuilder } from '../../config/surgeries-idika-search.config';
import { AuthService } from '../../../../services/auth/auth.service';
import { SurgeryAssignFormDialogComponent } from '../../../surgery/components/surgery-assign-form-dialog/surgery-assign-form-dialog.component';
import { Router } from '@angular/router';
import { Surgery } from '../../../core/models/surgery.model';
import { CoreService } from '../../../core/core.service';
import { SurgeryService } from '../../../../services/surgery/surgery.service';
import { Validators } from '@angular/forms';
import { ISortParams } from '../../../shared/models/sort.model';

@Component({
  selector: 'app-surgeries-idika-list',
  templateUrl: './surgeries-idika-list.component.html',
  styleUrl: './surgeries-idika-list.component.scss',
  providers: [
    SearchPageOptionsService,
    { provide: DATA_SERVICE_TOKEN, useClass: SurgeryIdikaService },
  ],
})
export class SurgeriesIdikaListComponent extends EoSearchPageComponent<IdikaSurgery> {
  @ViewChild('surgeryAssignFormDialog', { static: false })
  surgeryAssignFormDialog?: SurgeryAssignFormDialogComponent;

  hospitalId?: string | null;
  assignedSurgeriesCount: number = 0;
  unAssignedSurgeriesCount: number = 0;

  data$ = new BehaviorSubject<IdikaSurgery[]>([]);
  token$ = this.authService.token$;
  tokenData = this.token$.getValue();
  override tableConfig = this.getIdikaSurgeryTableConfig();

  protected getIdikaSurgeryTableConfig() {
    let data = this.searchService.searchCh.data$.getValue()?.resultset ?? [];
    data = this.mapIconClasses(data);
    return new IdikaSurgeriesTableConfigBuilder()
      .addAssignSurgeryFunction(this.assignSurgery.bind(this))
      .addIsAssignSurgeryBtnVisibleFunction(
        this.isAssignSurgeryBtnVisible.bind(this)
      )
      .addSurgeryStatusNameFunction(this.surgeryStatusNameCheck.bind(this))
      .addViewSurgeryFunction(this.viewSurgery.bind(this))
      .addIsViewSurgeryBtnVisibleFunction(
        this.isViewSurgeryBtnVisible.bind(this)
      )
      .addGetIndicationTooltipFunction(this.getIndicationTooltip.bind(this))
      .addReturnSurgeryFunction(this.returnSurgery.bind(this))
      .addIsReturnSurgeryBtnVisibleFunction(
        this.isReturnSurgeryBtnVisible.bind(this)
      )
      .addToPrivateClinicFuncion(this.toPrivateClinic.bind(this))
      .getTable(data);
  }

  private mapIconClasses(data: IdikaSurgery[]) {
    const checkIconClass = 'check-circle p-success';
    const timesIconClass = 'times-circle p-error';

    return data.map((x) => ({
      ...x,
      indicationIcon: x.hasCompletedSurgeries
        ? checkIconClass
        : x.hasInactiveSurgeries
        ? timesIconClass
        : '',
    }));
  }

  private getIndicationTooltip(surgery?: IdikaSurgery): string {
    if (surgery?.hasCompletedSurgeries) {
      return surgery.completeSurgeryIndicationMonths > 0
        ? `Για το συγκεκριμένο ΑΜΚΑ βρέθηκε ολοκληρωμένο χειρουργείο το τελευταίο ${surgery.completeSurgeryIndicationMonths}μηνο`
        : 'Για το συγκεκριμένο ΑΜΚΑ βρέθηκε ολοκληρωμένο χειρουργείο';
    }

    if (surgery?.hasInactiveSurgeries) {
      return 'Για το συγκεκριμένο ΑΜΚΑ βρέθηκαν παραπάνω από ένα απενεργοποιημένα χειρουργεία';
    }

    return '';
  }

  override defaultSearchParam = {
    hospitalID: '',
    clinicID: null as string | null,
    dateFrom: null as Date | null,
    dateTo: null as Date | null,
    patientAmka: null as string | null,
    status: null as string | null,
  };

  override searchParam = { ...this.defaultSearchParam };
  override formConfig = idikaSurgerySearchFormConfig;

  constructor(
    override searchService: SurgeryIdikaService,
    private authService: AuthService,
    private router: Router,
    private coreService: CoreService,
    private surgeryService: SurgeryService
  ) {
    super(searchService);
  }

  private surgeryStatusNameCheck(surgery?: IdikaSurgery): string {
    return surgery?.surgeryStatusAlias == 'canceled'
      ? this.getCanceledTooltipHtml(surgery)
      : '';
  }

  private viewSurgery(elx?: IdikaSurgery, $event?: any): void {
    if (elx?.surgeryId) {
      this.router.navigate(['surgery/view', elx?.surgeryId]);
    }
  }

  private assignSurgery(surgery?: IdikaSurgery, $event?: any): void {
    this.surgeryAssignFormDialog?.show(surgery, this.hospitalId ?? surgery?.hospitalID);
  }

  private isAssignSurgeryBtnVisible(surgery?: IdikaSurgery): boolean {
    return surgery?.surgeryStatusAlias.toLowerCase() === 'for_assignment';
  }

  private returnSurgery(surgery?: IdikaSurgery, $event?: any): void {
    this.confirmationService.confirm({
      target: $event.target,
      header: 'ΠΡΟΣΟΧΗ!',
      message: `Είστε σίγουροι ότι θέλετε να επιστρέψετε το συγκεκριμένο χειρουργείο σε «Προγραμματισμένο»; Η ενέργεια είναι αμετάκλητη.`,
      rejectLabel: 'Όχι',
      closeOnEscape: false,
      icon: 'pi pi-exclamation-triangle p-warning',
      acceptLabel: 'Ναι',
      rejectVisible: true,
      rejectButtonStyleClass: 'p-button-text',
      key: 'returnSurgeryPopup',
      accept: () => {
        return this.surgeryService
          .returnSurgery(surgery?.surgeryId ?? '')
          .subscribe(() => this.onGenericActionSuccess());
      },
    });
  }

  private toPrivateClinic(surgery?: IdikaSurgery, $event?: any): void {
    this.confirmationService.confirm({
      target: $event.target,
      header: 'ΠΡΟΣΟΧΗ!',
      message: `Είστε σίγουροι ότι θέλετε να μεταφέρετε το συγκεκριμένο χειρουργείο σε ιδιωτική δομή; Η ενέργεια είναι αμετάκλητη.`,
      rejectLabel: 'Όχι',
      closeOnEscape: false,
      icon: 'pi pi-exclamation-triangle p-warning',
      acceptLabel: 'Ναι',
      rejectVisible: true,
      rejectButtonStyleClass: 'p-button-text',
      key: 'toPrivateClinicPopup',
      accept: () => {
        return this.searchService
          .moveSurgeryToPrivateClinic(surgery?.id ?? '')
          .subscribe(() => this.onGenericActionSuccess());
      },
    });
  }

  private onGenericActionSuccess() {
    this.showGenericActionMessage();
    this.onSearch(null, this.searchService.getSavedSearchParams());
  }

  showGenericActionMessage(): void {
    this.coreService.showSuccess('Η ενέργεια ολοκληρώθηκε με επιτυχία!');
  }

  private isReturnSurgeryBtnVisible(surgery?: IdikaSurgery): boolean {
    return surgery?.surgeryStatusAlias.toLowerCase() === 'completed';
  }

  private isViewSurgeryBtnVisible(surgery?: IdikaSurgery): boolean {
    const status = surgery?.surgeryStatusAlias.toLowerCase();
    return (
      status !== 'for_assignment' &&
      status !== 'inactive' &&
      status !== 'noteligible'
    );
  }

  isAssignedSurgeriesLoading(): boolean {
    return (
      this.searchService.surgeryAssignedCountCallHandler.status$.getValue() ===
      STATUS.loading
    );
  }

  isUnAssignedSurgeriesLoading(): boolean {
    return (
      this.searchService.surgeryUnAssignedCountCallHandler.status$.getValue() ===
      STATUS.loading
    );
  }

  isSurgeryReturnLoading(): boolean {
    return (
      this.surgeryService.surgeryReturnCallHandler.status$.getValue() ===
      STATUS.loading
    );
  }

  isSurgeryMoveToPrivateClinicLoading(): boolean {
    return (
      this.searchService.surgeryMoveToPrivateClinicCallHandler.status$.getValue() ===
      STATUS.loading
    );
  }

  isSurgeryLoading(): boolean {
    return (
      this.isLoading() ||
      this.isSurgeryReturnLoading() ||
      this.isSurgeryMoveToPrivateClinicLoading()
    );
  }

  fetchAssignedSurgeriesCount(
    hospitalId?: string,
    clinicId?: string,
    patientAmka?: string
  ): void {
    if (!hospitalId && !patientAmka) {
      return;
    }
    this.searchService
      .getAssignedCount(hospitalId, clinicId, patientAmka)
      .subscribe((count: number) => (this.assignedSurgeriesCount = count));
  }

  fetchUnassignedSurgeriesCount(
    hospitalId?: string,
    clinicId?: string,
    patientAmka?: string
  ): void {
    if (!hospitalId && !patientAmka) {
      return;
    }
    this.searchService
      .getUnAssignedCount(hospitalId, clinicId, patientAmka)
      .subscribe((count: number) => (this.unAssignedSurgeriesCount = count));
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.updateHospitalIDControlValidator(null);
    this.searchService.searchCh.data$.subscribe(() => {
      if (this.tableConfig) {
        this.tableConfig = this.getIdikaSurgeryTableConfig();
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const savedSearchParams = this.searchService.getSavedSearchParams();
      if (savedSearchParams) {
        this.hospitalId = savedSearchParams.hospitalID;
        setSearchParams(savedSearchParams);
        this.checkPatientAmka(savedSearchParams);

        if (this.hospitalId || savedSearchParams.patientAmka) {
          this.onSearch(null, savedSearchParams);
        }
      } else {
        initSurgerySearchForm();
      }
    });
  }

  private checkPatientAmka(savedSearchParams: {
    hospitalID: string;
    clinicID: string | null;
    dateFrom: Date | null;
    dateTo: Date | null;
    patientAmka: string | null;
    status: string | null;
    sortParams?:
      | ISortParams
      | null;
  }) {
    if (!savedSearchParams?.patientAmka) {
      addRequiredValidator('form', 'hospitalID');
    } else {
      clearValidators('form', 'hospitalID');
    }
  }

  onSearch(searchParams?: any, savedSearchParams?: any) {
    this.searchParam =
      searchParams?.form || savedSearchParams || this.defaultSearchParam;
    this.hospitalId = this.searchParam.hospitalID;

    if (!this.searchParam.hospitalID && !this.searchParam.patientAmka) {
      return;
    }

    this.searchService
      .searchIdikaSurgeries(
      this.searchParam,
      this.getPagesize(),
      this.getPage()
      )
      .subscribe();

    this.fetchAssignedSurgeriesCount(
      this.searchParam.hospitalID,
      this.searchParam.clinicID || undefined,
      this.searchParam.patientAmka || undefined
    );

    this.fetchUnassignedSurgeriesCount(
      this.searchParam.hospitalID,
      this.searchParam.clinicID || undefined,
      this.searchParam.patientAmka || undefined
    );
  }

  override getTotalCount(): number {
    return this.searchService.searchCh.data$.getValue()?.totalCount ?? 0;
  }

  onHospitalChange($event: any) {
    this.hospitalId = $event.value || null; // Set hospitalId to selected hospital ID
    clearClinicDropdown();
  }

  onAssignSurgeryFormSuccess() {
    this.onSearch(null, this.searchService.getSavedSearchParams());
  }

  getCanceledTooltipHtml(surgery: IdikaSurgery): string {
    return `<strong>Αιτιολογία</strong>: ${
      surgery?.cancelReason
    }<br/><strong>Σχόλιο</strong>: ${surgery?.cancelComments ?? '-'}`;
  }

  updateHospitalIDControlValidator($event: any) {
    const inputValue = $event?.target?.value?.trim();
    if (!inputValue) {
      addRequiredValidator('form', 'hospitalID');
    } else {
      clearValidators('form', 'hospitalID');
    }
  }
}
